import * as React from "react"
import { Head, Link, usePage } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { ListChecks, Plus, Sparkles } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { EmptyState } from "@/components/blocks/EmptyState"
import { DeckFormDialog } from "@/components/flashcards/DeckFormDialog"
import { QuizList } from "@/components/quizzes/QuizList"
import { UnlockPrompt } from "@/components/UnlockPrompt"
import { Button } from "@/components/ui/button"
import type { PageProps } from "@/types/inertia"
import type { QuizSummary } from "@/types/quizzes"

export default function QuizzesIndex({
  official_quizzes,
  my_quizzes,
}: {
  official_quizzes: QuizSummary[]
  my_quizzes: QuizSummary[]
}) {
  const intl = useIntl()
  const [creating, setCreating] = React.useState(false)
  const programAccess = usePage<PageProps>().props.current_user?.program_access ?? false
  const quizHref = (quiz: QuizSummary) => `/quizzes/${quiz.id}`

  return (
    <>
      <Head title={intl.formatMessage({ id: "quizzes.title" })}>
        <meta name="description" content={intl.formatMessage({ id: "quizzes.index_meta" })} />
        <meta property="og:title" content={intl.formatMessage({ id: "quizzes.title" })} />
        <meta property="og:description" content={intl.formatMessage({ id: "quizzes.index_meta" })} />
      </Head>
      <AppShell>
        <PageHeader
          title={intl.formatMessage({ id: "quizzes.title" })}
          description={intl.formatMessage({ id: "quizzes.subtitle" })}
          actions={
            <>
              <Button type="button" variant="secondary" onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" /> {intl.formatMessage({ id: "quizzes.new_quiz" })}
              </Button>
              <Button asChild>
                <Link href="/generations/new?kind=quiz">
                  <Sparkles className="h-4 w-4" /> {intl.formatMessage({ id: "flashcards.generate_ai" })}
                </Link>
              </Button>
            </>
          }
        />

        {(programAccess || official_quizzes.length > 0) && (
          <section className="mt-10">
            <h2>{intl.formatMessage({ id: "quizzes.official_quizzes" })}</h2>
            {official_quizzes.length === 0 ? (
              <p className="mt-2">{intl.formatMessage({ id: "quizzes.official_soon" })}</p>
            ) : (
              <div className="mt-4">
                <QuizList quizzes={official_quizzes} hrefFor={quizHref} />
              </div>
            )}
          </section>
        )}

        <section className="mt-10">
          <h2>{intl.formatMessage({ id: "quizzes.my_quizzes" })}</h2>
          {my_quizzes.length === 0 ? (
            <div className="mt-4">
              <EmptyState
                icon={ListChecks}
                title={intl.formatMessage({ id: "quizzes.empty_title" })}
                description={intl.formatMessage({ id: "quizzes.empty_body" })}
                action={
                  <Button type="button" onClick={() => setCreating(true)}>
                    <Plus className="h-4 w-4" /> {intl.formatMessage({ id: "quizzes.new_quiz" })}
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="mt-4">
              <QuizList quizzes={my_quizzes} hrefFor={quizHref} />
            </div>
          )}
        </section>

        {!programAccess && official_quizzes.length === 0 && (
          <section className="mt-10">
            <h2>{intl.formatMessage({ id: "quizzes.official_quizzes" })}</h2>
            <UnlockPrompt what={intl.formatMessage({ id: "quizzes.unlock_what" })} />
          </section>
        )}

        <DeckFormDialog
          open={creating}
          onOpenChange={setCreating}
          action="/quizzes"
          method="post"
          title={intl.formatMessage({ id: "quizzes.new_quiz_title" })}
          description={intl.formatMessage({ id: "quizzes.new_quiz_description" })}
          submitLabel={intl.formatMessage({ id: "quizzes.create_quiz" })}
        />
      </AppShell>
    </>
  )
}
